/**
 * FeedWorkspace - Inside a Feed (Complete Feature Parity with FeedHome Results View)
 * 
 * Features:
 * - List/Kanban view toggle
 * - Inline filtering
 * - Bulk actions with outreach generation
 * - Export/Share/Duplicate
 * - Full lead detail panel with editable fields
 * - Custom fields
 * - Mobile responsive
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// BulkOutreachModal removed — outreach is now managed in Reach
import { 
  ArrowLeft,
  Settings,
  Share2,
  Bell,
  BellOff,
  Loader2,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  Check,
  Trash2,
  Star,
  Sparkles,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertCircle,
  Send,
  RefreshCw,
  Filter,
  LayoutList,
  Columns,
  Download,
  Copy,
  Layers,
  Search,
  MapPin,
  Building2,
  Briefcase,
  X,
  PlusCircle,
  Wand2,
  MoreVertical,
  CheckCircle2,
  XCircle,
  UserCircle,
  Globe,
  DollarSign,
  Users,
  Pencil,
  Save,
  GraduationCap,
  History,
  StickyNote,
  PlusSquare,
  MinusCircle,
  MessageSquare,
  BarChart3,
  FileText,
  Sliders,
  ChevronLeft
} from 'lucide-react';
import api from '../../utils/axios';
import AgentGrid from './AgentGrid';
import Dashboard from '../scout/Dashboard';
import RefineCoPilot from '../search/RefineCoPilot';
import RefinePanelOpener from '../search/RefinePanelOpener';
import LoadMoreFooter from '../search/LoadMoreFooter';
import { SearchProvider } from '../../contexts/SearchContext';
import SplitPaneLayout from '../layout/SplitPaneLayout';
import UniversalShareButton from '../shared/UniversalShareButton';
import { convertKeyElementsToFilters } from '../../utils/extractKeyElements';
import { LeadCard, LEAD_STATUSES as SHARED_LEAD_STATUSES } from '../shared/LeadCard';
import { LeadDetailPanel } from '../shared/LeadDetailPanel';
import { useToast } from '../shared/Toast';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useSubscription } from '../../contexts/SubscriptionContext';

// Lead status definitions - 4 categories matching LeadCard
const LEAD_STATUSES = [
  { id: 'inbox', label: 'Inbox', color: 'bg-purple-500', bgLight: 'bg-purple-50' },
  { id: 'reviewing', label: 'Reviewing', color: 'bg-blue-500', bgLight: 'bg-blue-50' },
  { id: 'shortlist', label: 'Shortlist', color: 'bg-emerald-500', bgLight: 'bg-emerald-50' },
  { id: 'archived', label: 'Archived', color: 'bg-slate-500', bgLight: 'bg-slate-50' },
];

export default function FeedWorkspace() {
  const { feedId } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const toast = useToast();
  const { isTeamPlan } = useSubscription();

  // Core State
  const [feed, setFeed] = useState(null);
  const [leads, setLeads] = useState([]);
  const [allFeeds, setAllFeeds] = useState([]);
  const [isLoadingFeed, setIsLoadingFeed] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isRefining, setIsRefining] = useState(false);
  const [error, setError] = useState(null);
  const [loadingAgentId, setLoadingAgentId] = useState(null); // For sidebar loading states

  // View & Filter State
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [resultTypeFilter, setResultTypeFilter] = useState('all'); // 'all', 'companies', 'contacts'
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', location: '', company: '', title: '', status: '' });
  
  // Detect if this is a signals/demand search - no full profile panel needed
  const isSignalsSearch = feed?.search_type === 'demand' || 
    feed?.search_criteria?.mode === 'demand' || 
    feed?.searchMode === 'demand' ||
    feed?.lead_type === 'signal' || 
    feed?.lead_type === 'signals';
  const feedCriteria = feed?.search_criteria || feed?.searchCriteria || {};
  const normalizeMode = (value) => {
    if (!value) return 'people';
    if (value === 'people' || value === 'contacts') return 'people';
    if (value === 'candidate' || value === 'client' || value === 'clients') return 'people'; // backward compat
    if (value === 'companies') return 'company';
    if (value === 'signal' || value === 'signals') return 'demand';
    if (value === 'job') return 'jobs';
    return value;
  };

  const parseRawData = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return null;
  };
  const feedMode = normalizeMode(
    feedCriteria.mode || feed?.searchMode || feed?.search_mode || feed?.search_type || feed?.lead_type
  );
  const feedTitle = feed?.natural_query || feed?.query || feed?.name || 'Untitled Agent';
  const feedDisplayName = feed?.name && feed?.name !== feedTitle ? feed.name : null;
  const feedModeLabelMap = {
    people: 'People',
    candidate: 'People',
    client: 'People',
    company: 'Companies',
    jobs: 'Jobs',
    demand: 'Signals',
  };
  const feedModeLabel = feedModeLabelMap[feedMode] || 'Search';
  
  // Refine panel - unified control for editing search
  const [shouldOpenRefinePanel, setShouldOpenRefinePanel] = useState(false);
  const [refineQuery, setRefineQuery] = useState('');
  const [refinePillFilters, setRefinePillFilters] = useState([]);
  const [refinePillOutputFields, setRefinePillOutputFields] = useState([]);
  const [listSize, setListSize] = useState(25);
  const [listMode, setListMode] = useState('short');
  const [loadedCount, setLoadedCount] = useState(25);
  const [hasMore, setHasMore] = useState(true);
  
  // Server-side pagination state
  const [serverOffset, setServerOffset] = useState(0);
  const [totalServerResults, setTotalServerResults] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 100; // Number of results per server request
  
  // Bulk Actions State
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState({});
  
  // Outreach Modal State
  const [isBulkAddingToReach, setIsBulkAddingToReach] = useState(false);
  
  // Status filter and progress counts - GLOBAL across all result pages
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'liked' | 'archived' | 'inbox'
  const [processedCount, setProcessedCount] = useState({ liked: 0, archived: 0 });
  
  // Share Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isSharing, setIsSharing] = useState(false);

  // Workspace (org) sharing
  const [isSharedWithOrg, setIsSharedWithOrg] = useState(false);
  const [isTogglingOrgShare, setIsTogglingOrgShare] = useState(false);
  
  // Dashboard State
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Custom Fields
  const [customFields, setCustomFields] = useState([]);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  
  // Mobile State - use hook for reliable detection
  const isMobile = useIsMobile();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [mobileKanbanTab, setMobileKanbanTab] = useState('new');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Signal Preview Modal State
  const [signalPreview, setSignalPreview] = useState(null);

  // =============================================================================
  // SAFETY: Global ESC key handler - close any open modal/dropdown
  // User should NEVER feel stuck - ESC always provides escape
  // =============================================================================
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        // Close in priority order: modals first, then panels, then navigate back
        if (signalPreview) { setSignalPreview(null); return; }
        if (showShareModal) { setShowShareModal(false); return; }
        if (showDashboard) { setShowDashboard(false); return; }
        if (showCustomFieldModal) { setShowCustomFieldModal(false); return; }
        if (showMobileMenu) { setShowMobileMenu(false); return; }
        if (showMobileSidebar) { setShowMobileSidebar(false); return; }
        if (showBulkActions) { setShowBulkActions(false); setSelectedLeads([]); return; }
        if (selectedLead) { setSelectedLead(null); return; }
        // If nothing open, go back to main app
        navigate('/app');
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [signalPreview, showShareModal, showDashboard,
      showCustomFieldModal, showMobileMenu, showMobileSidebar, 
      showBulkActions, selectedLead, navigate]);

  // Refs to hold latest versions of handlers (avoids stale closure issues)
  const handleExportCSVRef = useRef(null);
  const handleShareRef = useRef(null);
  const handleRefreshFeedRef = useRef(null);

  // Listen for sidebar events - uses refs to always call latest handler versions
  useEffect(() => {
    const handleOpenRefine = () => {
      setShouldOpenRefinePanel(true);
    };
    const handleExportCSVEvent = () => {
      handleExportCSVRef.current?.();
    };
    const handleShareEvent = () => {
      handleShareRef.current?.();
    };
    const handleToggleBulkActions = () => {
      setShowBulkActions(!showBulkActions);
    };
    const handleRefreshFeedEvent = () => {
      handleRefreshFeedRef.current?.();
    };
    const handleShowStats = () => {
      navigate(`/app/settings?tab=stats&feed=${feedId}`);
    };

    window.addEventListener('eq:open-refine', handleOpenRefine);
    window.addEventListener('eq:export-csv', handleExportCSVEvent);
    window.addEventListener('eq:share-list', handleShareEvent);
    window.addEventListener('eq:toggle-bulk-actions', handleToggleBulkActions);
    window.addEventListener('eq:refresh-feed', handleRefreshFeedEvent);
    window.addEventListener('eq:show-stats', handleShowStats);

    return () => {
      window.removeEventListener('eq:open-refine', handleOpenRefine);
      window.removeEventListener('eq:export-csv', handleExportCSVEvent);
      window.removeEventListener('eq:share-list', handleShareEvent);
      window.removeEventListener('eq:toggle-bulk-actions', handleToggleBulkActions);
      window.removeEventListener('eq:refresh-feed', handleRefreshFeedEvent);
      window.removeEventListener('eq:show-stats', handleShowStats);
    };
  }, [feedId, showBulkActions, navigate]);

  // Fetch feed details and leads
  const fetchFeed = useCallback(async () => {
    if (!feedId) return;
    try {
      setIsLoadingFeed(true);
      setError(null);
      // Reset pagination state for new feed
      setServerOffset(0);
      const response = await api.get(`/api/saved-searches/${feedId}?limit=${PAGE_SIZE}&offset=0`);
      if (response.data?.success && response.data?.savedSearch) {
        const savedSearch = response.data.savedSearch;
        setFeed(savedSearch);
        setIsSharedWithOrg(savedSearch.visibility === 'org');

        // Track server pagination info
        const pagination = savedSearch.pagination || {};
        const serverTotal = pagination.total || 0;
        setTotalServerResults(serverTotal);
        setServerOffset(PAGE_SIZE); // Next fetch starts at PAGE_SIZE
        
        // Combine both leads AND companies - company searches return results in 'companies'
        const fetchedLeads = savedSearch.leads || [];
        const fetchedCompanies = savedSearch.companies || [];
        
        // Merge them - companies first if it's a company search, otherwise leads first
        const isCompanySearch = savedSearch.search_type === 'company' || 
          savedSearch.search_criteria?.mode === 'company' ||
          fetchedCompanies.length > 0 && fetchedLeads.length === 0;
        
        const allResults = isCompanySearch 
          ? [...fetchedCompanies, ...fetchedLeads]
          : [...fetchedLeads, ...fetchedCompanies];
        
        // Fallback to results if neither has data
        const finalResults = allResults.length > 0 ? allResults : (savedSearch.results || []);
        setLeads(finalResults);
        // Initialize loaded count based on list size preference
        const initialCount = Math.min(listSize, finalResults.length);
        setLoadedCount(initialCount);
        // Check both local AND server pagination
        const hasMoreLocal = finalResults.length > initialCount;
        const hasMoreServer = pagination.hasMore || finalResults.length < serverTotal;
        setHasMore(hasMoreLocal || hasMoreServer);
        
        // Initialize statuses from lead data
        const statuses = {};
        finalResults.forEach(lead => {
          const id = lead.id || lead.personId || lead.lead_id || lead.companyId;
          statuses[id] = lead.status || 'inbox';
        });
        setLeadStatuses(statuses);
        
        // Note: Empty state is handled inline in the UI, not via toast
        // This prevents toast spam when navigating between agents
      } else {
        setError('Agent not found or has been deleted');
        toast.error('Agent not found', 'This agent may have been deleted or is unavailable.');
      }
    } catch (err) {
      console.error('Failed to fetch feed:', err);
      const errorMessage = err.response?.status === 404 
        ? 'Agent not found or has been deleted'
        : 'Failed to load agent. Please try again.';
      setError(errorMessage);
      toast.error('Failed to load agent', errorMessage, {
        onRetry: fetchFeed
      });
    } finally {
      setIsLoadingFeed(false);
    }
  }, [feedId, toast]);
  
  // Fetch more leads from server (pagination)
  const fetchMoreLeads = useCallback(async () => {
    if (!feedId || isLoadingMore || leads.length >= totalServerResults) return;
    
    try {
      setIsLoadingMore(true);
      const response = await api.get(`/api/saved-searches/${feedId}?limit=${PAGE_SIZE}&offset=${serverOffset}`);
      
      if (response.data?.success && response.data?.savedSearch) {
        const savedSearch = response.data.savedSearch;
        const fetchedLeads = savedSearch.leads || [];
        const fetchedCompanies = savedSearch.companies || [];
        
        // Merge new results - same logic as initial fetch
        const isCompanySearch = savedSearch.search_type === 'company' || 
          savedSearch.search_criteria?.mode === 'company' ||
          fetchedCompanies.length > 0 && fetchedLeads.length === 0;
        
        const newResults = isCompanySearch 
          ? [...fetchedCompanies, ...fetchedLeads]
          : [...fetchedLeads, ...fetchedCompanies];
        
        if (newResults.length > 0) {
          // Append to existing leads
          setLeads(prev => [...prev, ...newResults]);
          setServerOffset(prev => prev + PAGE_SIZE);
          
          // Initialize statuses for new leads
          setLeadStatuses(prev => {
            const newStatuses = { ...prev };
            newResults.forEach(lead => {
              const id = lead.id || lead.personId || lead.lead_id || lead.companyId;
              if (!newStatuses[id]) {
                newStatuses[id] = lead.status || 'inbox';
              }
            });
            return newStatuses;
          });
          
          // Update pagination info
          const pagination = savedSearch.pagination || {};
          setHasMore(pagination.hasMore || (serverOffset + newResults.length) < totalServerResults);
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch more leads:', err);
      toast.error('Failed to load more results', 'Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [feedId, serverOffset, totalServerResults, isLoadingMore, leads.length, toast]);

  // Fetch all feeds for sidebar
  const fetchAllFeeds = useCallback(async () => {
    try {
      const response = await api.get('/api/saved-searches');
      if (response.data?.success && response.data?.savedSearches) {
        setAllFeeds(response.data.savedSearches);
      }
    } catch (err) {
      console.error('Failed to fetch feeds:', err);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
    fetchAllFeeds();
  }, [fetchFeed, fetchAllFeeds]);
  
  useEffect(() => {
    if (!feed) return;
    setRefineQuery(feed?.natural_query || feed?.name || '');
    setRefinePillFilters(feedCriteria.filters || []);
    setRefinePillOutputFields(feedCriteria.outputFields || []);
  }, [feedId, feed, feedCriteria.filters, feedCriteria.outputFields]);

  // =============================================================================
  // FILTERING & SORTING - Archived leads are hidden, liked leads stay visible
  // =============================================================================
  const filteredLeads = useMemo(() => {
    if (!leads.length) return [];
    
    // Filter: apply user filters AND status filter from header tabs
    const filtered = leads.filter(lead => {
      const data = lead.data || lead.lead_data || lead;
      const fullName = (data.name || data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim()).toLowerCase();
      const jobTitle = (data.jobTitle || data.title || '').toLowerCase();
      const companyName = (data.companyName || data.company_name || '').toLowerCase();
      const location = (data.city || data.location || data.state || '').toLowerCase();
      const id = lead.id || lead.personId || lead.lead_id || lead.companyId;
      const leadStatus = leadStatuses[id] || 'inbox';
      
      // Status filter from header tabs (All | ✓ Liked | ✗ Archived)
      if (statusFilter !== 'all' && leadStatus !== statusFilter) {
        return false;
      }
      
      // Determine if this is a company or contact result
      const isCompany = lead.companyId || data.companyId || (!data.firstName && !data.lastName && !data.email && data.companyName);
      
      // Filter by result type (companies vs contacts)
      if (resultTypeFilter === 'companies' && !isCompany) return false;
      if (resultTypeFilter === 'contacts' && isCompany) return false;
      
      if (filters.search && !fullName.includes(filters.search.toLowerCase()) && 
          !jobTitle.includes(filters.search.toLowerCase()) && 
          !companyName.includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.location && !location.includes(filters.location.toLowerCase())) return false;
      if (filters.company && !companyName.includes(filters.company.toLowerCase())) return false;
      if (filters.title && !jobTitle.includes(filters.title.toLowerCase())) return false;
      
      return true;
    });
    
    // Sort: LIKED leads first (most important), then inbox (unprocessed), then archived at bottom
    return filtered.sort((a, b) => {
      const idA = a.id || a.personId || a.lead_id || a.companyId;
      const idB = b.id || b.personId || b.lead_id || b.companyId;
      const statusA = leadStatuses[idA] || 'inbox';
      const statusB = leadStatuses[idB] || 'inbox';
      
      // Priority: liked/shortlist (0) > inbox (1) > archived (2)
      // Liked leads are the user's selections - they should always be at the top
      const priority = { liked: 0, shortlist: 0, inbox: 1, archived: 2 };
      return (priority[statusA] ?? 1) - (priority[statusB] ?? 1);
    });
  }, [leads, filters, leadStatuses, resultTypeFilter, statusFilter]);
  
  const checkedProfiles = useMemo(() => {
    return filteredLeads.filter(lead => {
      const id = lead.id || lead.personId || lead.lead_id || lead.companyId;
      const status = leadStatuses[id];
      return status === 'shortlist' || status === 'liked';
    });
  }, [filteredLeads, leadStatuses]);
  
  const activeRefineFilters = (refinePillFilters.length > 0 ? refinePillFilters : (feedCriteria.filters || []))
    .filter(f => Array.isArray(f?.pills));
  const activeRefineOutputFields = refinePillOutputFields.length > 0 ? refinePillOutputFields : (feedCriteria.outputFields || []);
  
  // Count companies and contacts for toggle
  const resultCounts = useMemo(() => {
    let companies = 0;
    let contacts = 0;
    leads.forEach(lead => {
      const data = lead.data || lead.lead_data || lead;
      const isCompany = lead.companyId || data.companyId || (!data.firstName && !data.lastName && !data.email && data.companyName);
      if (isCompany) companies++;
      else contacts++;
    });
    // Use server total if available, otherwise use loaded leads count
    return { companies, contacts, total: totalServerResults || leads.length };
  }, [leads, totalServerResults]);

  // Group leads by status for Kanban
  const kanbanColumns = useMemo(() => {
    const columns = {};
    LEAD_STATUSES.forEach(status => {
      columns[status.id] = [];
    });
    
    filteredLeads.forEach(lead => {
      const id = lead.id || lead.personId || lead.lead_id;
      const status = leadStatuses[id] || 'inbox';
      if (columns[status]) {
        columns[status].push(lead);
      } else {
        columns['inbox'].push(lead);
      }
    });
    
    return columns;
  }, [filteredLeads, leadStatuses]);

  const clearFilters = () => {
    setFilters({ search: '', location: '', company: '', title: '', status: '' });
  };

  // =============================================================================
  // STATUS & LEAD ACTIONS - Track counts for header display
  // =============================================================================
  const updateLeadStatus = async (leadId, newStatus) => {
    const oldStatus = leadStatuses[leadId] || 'inbox';
    // Update status
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    
    // Update counts only for new actions
    if (oldStatus !== newStatus) {
      setProcessedCount(prev => {
        const newCounts = { ...prev };
        if (newStatus === 'liked') newCounts.liked += 1;
        if (newStatus === 'archived') newCounts.archived += 1;
        return newCounts;
      });
    }
    
    try {
      await api.patch(`/api/leads/${leadId}`, { status: newStatus });
    } catch (err) {
      console.error('Failed to update lead status:', err);
    }
  };

  const handleToggleWatch = async () => {
    if (!feed) return;

    try {
      const response = await api.post(`/api/leads/alerts/${feedId}/toggle`, {
        isAlert: !feed.is_alert,
        frequency: 'daily',
        scoutConfig: feed.scout_config || {}
      });

      if (response.data?.success) {
        setFeed(prev => ({ ...prev, is_alert: !prev.is_alert }));
      }
    } catch (err) {
      console.error('Failed to toggle watch:', err);
    }
  };

  const runFeedSearch = async ({
    query: overrideQuery,
    mode: overrideMode,
    filters: overrideFilters,
    outputFields: overrideOutputFields,
    listSize: overrideListSize,
    rankingWeights: overrideRankingWeights,
  } = {}) => {
    if (!feedId || !feed) return;
    
    const savedSearchId = parseInt(feedId, 10);
    const resolvedQuery = (overrideQuery || feed?.natural_query || feed?.name || '').trim();
    const resolvedMode = normalizeMode(
      overrideMode || feedCriteria.mode || feed?.searchMode || feed?.search_mode || feed?.search_type
    );
    const rawFilters = Array.isArray(overrideFilters) ? overrideFilters : (feedCriteria.filters || []);
    const resolvedFilters = Array.isArray(rawFilters) ? rawFilters.filter(f => Array.isArray(f.pills)) : [];
    const resolvedOutputFields = Array.isArray(overrideOutputFields) ? overrideOutputFields : (feedCriteria.outputFields || []);
    const resolvedRankingWeights = overrideRankingWeights || feedCriteria.rankingWeights || null;
    // Always use at least 100 to get more results (ZoomInfo's typical max per request)
    const rpp = Math.max(overrideListSize || feedCriteria.listSize || 0, 100);
    
    const isWebsiteWatch = feed.scout_config?.strategy === 'scrape' || 
      feed.search_criteria?.isWebsiteWatch ||
      (feed.scout_config?.websites && feed.scout_config.websites.length > 0);
    
    if (isWebsiteWatch) {
      await api.post(`/api/scout/run/${feedId}`);
      return;
    }
    
    if (resolvedMode === 'jobs' || resolvedMode === 'demand') {
      const fallbackFilters = resolvedFilters.length > 0 ? resolvedFilters : [{
        id: 'keywords',
        apiField: 'q',
        pills: [{ id: 'kw-0', label: resolvedQuery, status: 'included', apiValue: resolvedQuery }],
      }];
      await api.post('/api/ai/execute-pill-search', {
        filters: fallbackFilters,
        outputFields: resolvedOutputFields,
        mode: resolvedMode,
        rpp,
        savedSearchId,
        rankingWeights: resolvedRankingWeights || undefined,
        replaceResults: true,
      });
      return;
    }
    
    if (resolvedFilters.length > 0) {
      await api.post('/api/ai/execute-pill-search', {
        filters: resolvedFilters,
        outputFields: resolvedOutputFields,
        mode: resolvedMode === 'company' ? 'company' : resolvedMode,
        rpp,
        savedSearchId,
        rankingWeights: resolvedRankingWeights || undefined,
        replaceResults: true,
      });
      return;
    }
    
    if (resolvedMode === 'company') {
      await api.post('/api/ai/ai-company-search', {
        query: resolvedQuery,
        rpp,
        savedSearchId,
      });
      return;
    }
    
    await api.post('/api/ai/ai-people-search', {
      query: resolvedQuery,
      rpp,
      searchMode: resolvedMode,
      savedSearchId,
    });
  };

  const handleRefreshFeed = useCallback(async () => {
    if (!feedId || !feed) return;
    
    try {
      setIsRefining(true);
      await runFeedSearch();
      await fetchFeed();
    } catch (err) {
      console.error('Failed to refresh feed:', err);
    } finally {
      setIsRefining(false);
    }
  }, [feedId, feed, runFeedSearch, fetchFeed]);
  
  // Keep ref in sync with latest callback
  useEffect(() => { handleRefreshFeedRef.current = handleRefreshFeed; }, [handleRefreshFeed]);

  const handleApplyRefinement = async ({
    query,
    mode,
    filters = [],
    outputFields = [],
    weights,
    listSize: nextListSize,
    listMode: nextListMode,
  } = {}) => {
    if (!feedId || !feed) {
      console.warn('⚠️ FeedWorkspace: Cannot apply refinement - no feed loaded');
      return;
    }
    
    const nextQuery = (query || feed?.natural_query || feed?.name || '').trim();
    const resolvedMode = normalizeMode(mode || feedMode);
    // Always use at least 100 to get more results (ZoomInfo's typical max per request)
    const rpp = Math.max(nextListSize || listSize || 0, 100);
    const resolvedListMode = nextListMode || listMode;
    
    const sanitizedFilters = Array.isArray(filters) ? filters.filter(f => Array.isArray(f.pills)) : [];
    
    // Log what we're searching with
    console.log('🔄 FeedWorkspace: Applying refinement', {
      feedId,
      mode: resolvedMode,
      filtersCount: sanitizedFilters.length,
      includedFilters: sanitizedFilters.map(f => ({
        id: f.id,
        included: f.pills?.filter(p => p.status === 'included').map(p => p.label),
        excluded: f.pills?.filter(p => p.status === 'excluded').map(p => p.label),
      })),
      rpp,
    });
    
    setRefineQuery(nextQuery);
    setRefinePillFilters(sanitizedFilters);
    setRefinePillOutputFields(outputFields);
    setListSize(rpp);
    setListMode(resolvedListMode);
    
    try {
      setIsRefining(true);
      await api.patch(`/api/ai/saved-searches/${feedId}`, {
        naturalQuery: nextQuery,
        searchCriteria: {
          mode: resolvedMode,
          filters: sanitizedFilters,
          outputFields,
          rankingWeights: weights,
          listSize: rpp,
          listMode: resolvedListMode,
        },
      });
      await runFeedSearch({
        query: nextQuery,
        mode: resolvedMode,
        filters: sanitizedFilters,
        outputFields,
        listSize: rpp,
        rankingWeights: weights,
      });
      await fetchFeed();
      console.log('✅ FeedWorkspace: Refinement complete');
    } catch (err) {
      console.error('❌ FeedWorkspace: Failed to apply refinement:', err);
      toast.error('Refinement failed', err.message || 'Please try again');
    } finally {
      setIsRefining(false);
    }
  };

  const handleSaveAsNew = async ({
    query,
    mode,
    filters = [],
    outputFields = [],
    listSize: nextListSize,
    listMode: nextListMode,
  } = {}) => {
    const nextQuery = (query || feed?.natural_query || feed?.name || '').trim();
    const resolvedMode = normalizeMode(mode || feedMode);
    const resultsLimit = nextListSize || 25;
    
    try {
      const response = await api.post('/api/saved-searches', {
        searchCriteria: {
          mode: resolvedMode,
          filters,
          outputFields,
          listSize: resultsLimit,
          listMode: nextListMode || 'short',
        },
        leadType: resolvedMode === 'company' ? 'company' : 'contact',
        resultsLimit,
        customName: nextQuery || 'New Work',
        naturalQuery: nextQuery,
      });
      
      const newId = response.data?.savedSearch?.id || response.data?.savedSearchId;
      if (newId) {
        navigate(`/app/feed/${newId}`);
      }
    } catch (err) {
      console.error('Failed to create new feed:', err);
    }
  };

  const handleLeadAction = async (leadId, action) => {
    try {
      if (action === 'hide' || action === 'delete') {
        await api.delete(`/api/leads/${leadId}`);
        setLeads(prev => prev.filter(l => (l.id || l.personId || l.lead_id) !== leadId));
        if ((selectedLead?.id || selectedLead?.personId || selectedLead?.lead_id) === leadId) {
          setSelectedLead(null);
        }
      } else if (action === 'contacted') {
        await api.patch(`/api/leads/${leadId}`, { status: 'contacted' });
        setLeadStatuses(prev => ({ ...prev, [leadId]: 'contacted' }));
      } else if (action === 'star') {
        await api.patch(`/api/leads/${leadId}`, { starred: true });
        setLeads(prev => prev.map(l => 
          (l.id || l.personId || l.lead_id) === leadId 
            ? { ...l, starred: !l.starred } 
            : l
        ));
      }
    } catch (err) {
      console.error('Failed to update lead:', err);
    }
  };

  // =============================================================================
  // BULK ACTIONS
  // =============================================================================
  const toggleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      const newSelected = filteredLeads.map(l => l.id || l.personId || l.lead_id);
      setSelectedLeads(newSelected);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    const updates = {};
    selectedLeads.forEach(id => {
      updates[id] = newStatus;
    });
    setLeadStatuses(prev => ({ ...prev, ...updates }));
    
    // Update in background
    try {
      await Promise.all(
        selectedLeads.map(id => api.patch(`/api/leads/${id}`, { status: newStatus }))
      );
    } catch (err) {
      console.error('Bulk status update failed:', err);
    }
  };

  const handleBulkAddToReach = async () => {
    if (selectedLeads.length === 0) return;
    setIsBulkAddingToReach(true);
    let added = 0;
    for (const leadId of selectedLeads) {
      try {
        await api.post(`/api/leads/${leadId}/add-to-reach`);
        added++;
      } catch (err) {
        console.warn(`Failed to add lead ${leadId} to Reach:`, err.message);
      }
    }
    setIsBulkAddingToReach(false);
    toast.success(`${added} lead${added !== 1 ? 's' : ''} added to Reach`);
    setSelectedLeads([]);
    setShowBulkActions(false);
  };

  // =============================================================================
  // EXPORT
  // =============================================================================
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFieldGroups, setExportFieldGroups] = useState({
    extendedContact: false,
    careerHistory: false,
    companyDetails: false,
    formality: false,
  });
  const [isExporting, setIsExporting] = useState(false);

  const FIELD_GROUP_META = {
    extendedContact: { label: 'Extended Contact Info', description: 'Additional emails, GitHub, Twitter, LinkedIn headline' },
    careerHistory:   { label: 'Career History',        description: 'Skills, education, work history, languages' },
    companyDetails:  { label: 'Company Details',       description: 'Domain, phone, HQ location, company overview' },
    formality:       { label: 'Formality / Mail Merge', description: 'Salutation, middle name, suffix' },
  };

  const handleExportCSV = useCallback(() => {
    setShowExportModal(true);
  }, []);

  const doExportWithGroups = useCallback(async (overrideGroups) => {
    setIsExporting(true);
    try {
      const includeGroups = overrideGroups !== undefined
        ? overrideGroups
        : Object.entries(exportFieldGroups)
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);

      const enrichedContactsData = {};
      filteredLeads.forEach((lead, idx) => {
        const data = lead.data || lead;
        const id = data.id || data.person_id || data.personId || lead.lead_id || `lead_${idx}`;
        enrichedContactsData[id] = { ...data, enriched: true };
      });

      const selectedContacts = Object.keys(enrichedContactsData);

      const response = await api.post('/api/ai/export-leads', {
        selectedContacts,
        exportFormat: 'csv',
        sessionId: feedId || `workspace_${Date.now()}`,
        searchQuery: feed?.name || 'Feed Export',
        enrichedContactsData,
        includeGroups,
      });

      if (response.data?.success) {
        const blob = new Blob([response.data.exportData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = response.data.filename || `${feed?.name || 'leads'}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        setShowExportModal(false);
      }
    } catch (err) {
      console.error('Export failed:', err);
      const serverMsg = err?.response?.data?.error || '';
      if (err?.response?.status === 403 || serverMsg.toLowerCase().includes('upgrade')) {
        toast?.error?.('Upgrade required', serverMsg || 'Upgrade your plan to export leads.');
        window.dispatchEvent(new CustomEvent('eq_show_upgrade_modal', { detail: { reason: 'export' } }));
      } else {
        toast?.error?.('Export failed', 'Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  }, [filteredLeads, feed, feedId, exportFieldGroups, toast]);

  // Keep ref in sync with latest callback
  useEffect(() => { handleExportCSVRef.current = handleExportCSV; }, [handleExportCSV]);

  // =============================================================================
  // SHARE
  // =============================================================================
  const handleShare = useCallback(async () => {
    setIsSharing(true);
    try {
      // Request public share link - 'invite' requires an inviteCode which we don't have by default
      const response = await api.post(`/api/saved-searches/${feedId}/share-link`, {
        visibility: 'public'
      });
      
      // Backend returns shareLink object with slug/shareToken
      // URL format should be: {slug}--{token} or just {token}
      const shareData = response.data?.shareLink;
      if (shareData?.slug && shareData?.shareToken) {
        // Full format with slug for SEO-friendly URLs
        setShareLink(`${window.location.origin}/share/${shareData.slug}--${shareData.shareToken}`);
      } else if (shareData?.shareToken) {
        // Just token
        setShareLink(`${window.location.origin}/share/${shareData.shareToken}`);
      } else if (shareData?.slug) {
        // Fallback to slug only (shouldn't happen but handle it)
        setShareLink(`${window.location.origin}/share/${shareData.slug}`);
      } else if (response.data?.shareUrl) {
        setShareLink(response.data.shareUrl);
      } else {
        // Fallback
        setShareLink(`${window.location.origin}/share/feed/${feedId}`);
      }
      setShowShareModal(true);
    } catch (err) {
      console.error('Failed to create share link:', err);
      // Fallback link
      setShareLink(`${window.location.origin}/share/feed/${feedId}`);
      setShowShareModal(true);
    } finally {
      setIsSharing(false);
    }
  }, [feedId]);
  
  // Keep ref in sync with latest callback
  useEffect(() => { handleShareRef.current = handleShare; }, [handleShare]);

  // Toggle "Share with workspace" — makes this search visible to all org members
  const handleToggleOrgShare = useCallback(async () => {
    if (!feed?.id || isTogglingOrgShare) return;
    setIsTogglingOrgShare(true);
    try {
      const newShared = !isSharedWithOrg;
      await api.post(`/api/saved-searches/${feed.id}/share`, { shared: newShared });
      setIsSharedWithOrg(newShared);
      toast.success(newShared ? 'This search is now visible to your team' : 'This search is now private');
      window.dispatchEvent(new Event('eq:agent-updated'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update sharing');
    } finally {
      setIsTogglingOrgShare(false);
    }
  }, [feed?.id, isSharedWithOrg, isTogglingOrgShare, toast]);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // =============================================================================
  // DUPLICATE
  // =============================================================================
  const handleDuplicate = async () => {
    try {
      const response = await api.post('/api/saved-searches', {
        name: `${feed?.name || 'Feed'} (Copy)`,
        query: feed?.query || feed?.natural_query,
        searchParams: feed?.searchParams || feed?.search_params,
        searchMode: feed?.searchMode || feed?.search_mode || 'people',
      });

      if (response.data?.success && response.data?.savedSearch?.id) {
        navigate(`/app/feed/${response.data.savedSearch.id}`);
      }
    } catch (err) {
      console.error('Failed to duplicate:', err);
    }
  };

  // =============================================================================
  // DELETE
  // =============================================================================
  const handleDeleteFeed = async () => {
    if (!window.confirm('Delete this feed? This cannot be undone.')) return;
    
    try {
      await api.delete(`/api/saved-searches/${feedId}`);
      navigate('/app');
    } catch (err) {
      console.error('Failed to delete feed:', err);
    }
  };

  // Navigation with loading state
  const handleSelectFeed = async (f) => {
    setLoadingAgentId(f.id);
    try {
      // Pre-verify the feed exists before navigating
      const response = await api.get(`/api/saved-searches/${f.id}`);
      if (response.data?.success) {
        navigate(`/app/feed/${f.id}`);
      } else {
        toast.error('Could not load agent', 'This agent may have been deleted.');
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
      toast.error('Failed to load agent', 'Please try again.', {
        onRetry: () => handleSelectFeed(f)
      });
    } finally {
      setLoadingAgentId(null);
    }
  };

  // Loading state - Premium animation
  if (isLoadingFeed) {
    return (
      <div className="min-h-screen bg-[#f8f8f6] flex items-center justify-center relative overflow-hidden" style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', marginTop: '56px' }}>
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#52dcb3]/10 via-transparent to-[#5a20ff]/10" />
        
        <div 
          className="flex flex-col items-center gap-4 relative z-10"
          style={{ animation: 'eq-fade-in 500ms ease-out' }}
        >
          {/* Animated spinner with glow */}
          <div className="relative">
            <div 
              className="w-12 h-12 rounded-full border-2 border-[#5a20ff]/20"
              style={{
                borderTopColor: '#5a20ff',
                animation: 'eq-spin 1s cubic-bezier(0.65, 0, 0.35, 1) infinite',
              }}
            />
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(90,32,255,0.15) 0%, transparent 70%)',
                animation: 'eq-pulse 2s ease-in-out infinite',
              }}
            />
          </div>
          <p 
            className="text-sm text-black/50"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Loading agent...
          </p>
        </div>
        
        <style>{`
          @keyframes eq-spin { to { transform: rotate(360deg); } }
          @keyframes eq-pulse { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 1; transform: scale(1.2); } }
          @keyframes eq-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  // Error state with retry option - Premium styling
  if (error && !feed) {
    return (
      <div className="min-h-screen bg-[#f8f8f6] flex items-center justify-center p-4" style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', marginTop: '56px' }}>
        <div 
          className="max-w-md w-full bg-white/95 backdrop-blur-xl rounded-2xl border border-red-200 p-8 text-center shadow-xl"
          style={{ animation: 'eq-scale-in 300ms ease-out' }}
        >
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 
            className="text-xl font-bold text-black mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Could not load agent
          </h2>
          <p className="text-sm text-black/50 mb-6">
            {error}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                setError(null);
                fetchFeed();
              }}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#5a20ff] text-white rounded-full font-semibold hover:scale-105 active:scale-[0.98] shadow-lg shadow-[#5a20ff]/25 transition-all duration-200"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={() => navigate('/app')}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-black/70 border-2 border-black/10 rounded-full font-semibold hover:border-black/20 hover:scale-[1.02] transition-all duration-200"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>
        </div>
        
        <style>{`
          @keyframes eq-scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        `}</style>
      </div>
    );
  }

  return (
    <SearchProvider>
      <SplitPaneLayout
        leftPanel={
          <RefineCoPilot
            query={refineQuery || feed?.natural_query || feed?.name || ''}
            mode={feedMode}
            filters={activeRefineFilters}
            outputFields={activeRefineOutputFields}
            results={filteredLeads}
            checkedProfiles={checkedProfiles}
            onUpdate={handleApplyRefinement}
            onSaveAsNew={handleSaveAsNew}
          />
        }
      >
      <div className="min-h-screen md:h-screen w-full bg-[#f8f8f6] flex overflow-hidden relative" style={{ maxWidth: '100%' }}>
      <RefinePanelOpener
        shouldOpen={shouldOpenRefinePanel}
        onOpened={() => setShouldOpenRefinePanel(false)}
      />
      {/* Page enter animation */}
      <style>{`
        @keyframes eq-page-enter { from { opacity: 0; } to { opacity: 1; } }
        @keyframes eq-slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes eq-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes eq-fade-in { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      
      {/* Slide-out Agent Panel - Premium glass effect */}
      {showMobileSidebar && (
        <>
          {/* Backdrop with blur */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={() => setShowMobileSidebar(false)}
            style={{ animation: 'eq-page-enter 200ms ease-out' }}
          />
          {/* Panel - Premium styling */}
          <div 
            className="fixed top-0 left-0 h-full w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl z-50 flex flex-col"
            style={{ animation: 'eq-slide-in-left 300ms cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            {/* Panel Header with gradient accent */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-black/5 bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5 safe-area-top">
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="flex items-center gap-1.5 text-[#5a20ff] text-sm font-semibold hover:opacity-70 transition-opacity"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <h2 className="text-lg font-bold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your Agents
              </h2>
              <div className="w-16" />
            </div>
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto">
              <AgentGrid 
                agents={allFeeds}
                selectedAgentId={parseInt(feedId)}
                loadingAgentId={loadingAgentId}
                onSelectAgent={(agent) => {
                  handleSelectFeed(agent);
                  setShowMobileSidebar(false);
                }}
                onNewAgent={() => {
                  navigate('/app');
                  setShowMobileSidebar(false);
                }}
                onDeleteAgent={async (id) => {
                  await api.delete(`/api/saved-searches/${id}`);
                  fetchAllFeeds();
                  if (id === parseInt(feedId)) navigate('/app');
                }}
                onShareAgent={async (id) => {
                  const response = await api.post(`/api/saved-searches/${id}/share-link`);
                  const link = response.data?.shareUrl || `${window.location.origin}/share/feed/${id}`;
                  setShareLink(link);
                  setShowShareModal(true);
                }}
                viewMode="list"
                hideHeader={false}
              />
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Compact Status Bar - Centered Filters with Share Button */}
        <div className="bg-white/50 backdrop-blur-sm border-b border-black/5 px-2 sm:px-4 py-2 flex items-center gap-2 sm:gap-4 flex-shrink-0 overflow-x-auto" style={{ maxWidth: '100%' }}>
          {/* Status Filters - Scrollable on mobile */}
          <div className="flex-1 flex items-center justify-center min-w-0">
            <div className="flex items-center gap-1 px-2 sm:px-3 py-1 bg-black/[0.02] rounded-full">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  statusFilter === 'all' 
                    ? 'bg-white shadow-sm text-black' 
                    : 'text-black/40 hover:text-black/60'
                }`}
              >
                All ({totalServerResults || leads.length})
              </button>
              <button
                onClick={() => setStatusFilter('liked')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  statusFilter === 'liked' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'text-emerald-600/60 hover:text-emerald-600'
                }`}
              >
                <Check className="w-3 h-3" />
                <span className="hidden sm:inline">Liked</span>
              </button>
              <button
                onClick={() => setStatusFilter('archived')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  statusFilter === 'archived' 
                    ? 'bg-black/5 text-black/70' 
                    : 'text-black/30 hover:text-black/50'
                }`}
              >
                <X className="w-3 h-3" />
                <span className="hidden sm:inline">Skip</span>
              </button>
            </div>
          </div>
          
          {/* Workspace Share Toggle + External Share */}
          <div className="flex-shrink-0 flex items-center gap-1.5">
            {isTeamPlan && (
              <button
                onClick={handleToggleOrgShare}
                disabled={isTogglingOrgShare}
                title={isSharedWithOrg ? 'This search is shared with your team. Click to make it private.' : 'Share this search with your team. Members in your workspace will be able to view this list and its results.'}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isSharedWithOrg
                    ? 'bg-[#52dcb3]/15 text-[#52dcb3] border border-[#52dcb3]/30 hover:bg-[#52dcb3]/25'
                    : 'bg-black/5 text-black/50 hover:bg-black/10 hover:text-black/70'
                } ${isTogglingOrgShare ? 'opacity-50' : ''}`}
              >
                <Users className="w-3.5 h-3.5" />
                <span className="hidden sm:inline text-xs">{isSharedWithOrg ? 'Shared with Team' : 'Share with Team'}</span>
                {isSharedWithOrg && <Check className="w-3 h-3" />}
              </button>
            )}
            <UniversalShareButton
              savedSearchId={feed?.id}
              title={feed?.name || feed?.natural_query || 'My Research'}
              resultsCount={totalServerResults || leads.length}
              variant="header"
            />
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <div className="bg-white border-b border-slate-5 px-3 md:px-4 py-2 md:py-3 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search filter */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-5 rounded-lg flex-1 min-w-[150px] max-w-[250px]">
                <Search className="w-3.5 h-3.5 text-slate-40 flex-shrink-0" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search..."
                  className="flex-1 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none min-w-0"
                />
              </div>

              {/* Location filter */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-5 rounded-lg">
                <MapPin className="w-3.5 h-3.5 text-slate-40" />
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Location"
                  className="w-20 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                />
              </div>

              {/* Company filter */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-5 rounded-lg">
                <Building2 className="w-3.5 h-3.5 text-slate-40" />
                <input
                  type="text"
                  value={filters.company}
                  onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Company"
                  className="w-20 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                />
              </div>

              {/* Status filter */}
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-1.5 bg-slate-5 rounded-lg text-xs text-black focus:outline-none"
              >
                <option value="">All Status</option>
                {LEAD_STATUSES.map(status => (
                  <option key={status.id} value={status.id}>{status.label}</option>
                ))}
              </select>

              {/* Clear filters */}
              {Object.values(filters).some(f => f) && (
                <button
                  onClick={clearFilters}
                  className="px-2.5 py-1.5 text-xs text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/10 rounded-lg transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {showBulkActions && (
          <div className="bg-[var(--eq-purple)]/5 border-b border-[var(--eq-purple)]/10 px-3 md:px-4 py-2 flex-shrink-0">
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[var(--eq-purple)]/20 hover:bg-[var(--eq-purple)]/10 transition-colors"
              >
                <Check className={`w-3.5 h-3.5 ${selectedLeads.length === filteredLeads.length ? 'text-[var(--eq-purple-text)]' : 'text-slate-40'}`} />
                {selectedLeads.length === filteredLeads.length ? 'Deselect' : 'Select All'}
              </button>

              {selectedLeads.length > 0 && (
                <>
                  <span className="text-xs text-[var(--eq-purple-text)] font-medium">
                    {selectedLeads.length} selected
                  </span>

                  <select
                    onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)}
                    className="px-3 py-1.5 rounded-lg text-xs bg-white border border-[var(--eq-purple)]/20 focus:outline-none"
                    defaultValue=""
                  >
                    <option value="" disabled>Status</option>
                    {LEAD_STATUSES.map(status => (
                      <option key={status.id} value={status.id}>{status.label}</option>
                    ))}
                  </select>

                  <button
                    onClick={handleBulkAddToReach}
                    disabled={isBulkAddingToReach}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isBulkAddingToReach ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding...</>
                    ) : (
                      <><Send className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Add to Reach</span></>
                    )}
                  </button>
                </>
              )}

              <button
                onClick={() => { setShowBulkActions(false); setSelectedLeads([]); }}
                className="ml-auto text-xs text-slate-40 hover:text-slate-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div data-debug="content-area" className="flex-1 flex flex-col md:flex-row overflow-hidden relative" style={{ maxWidth: '100%', minWidth: 0 }}>
          {viewMode === 'list' ? (
            <>
              {/* Lead List - Always visible, drawer overlays on mobile */}
              <div data-debug="lead-list" className="flex-1 overflow-y-auto p-3 md:p-4 flex flex-col">
                {filteredLeads.length === 0 ? (
                  <EmptyState onRefresh={handleRefreshFeed} isRefreshing={isRefining} />
                ) : (
                  <>
                    <div className="space-y-2 max-w-3xl mx-auto flex-1">
                      {filteredLeads.slice(0, loadedCount).map((lead, index) => {
                        const id = lead.id || lead.personId || lead.lead_id || index;
                        const rawData = parseRawData(lead.raw_data || lead.rawData);
                        const leadData = lead.data || lead.lead_data || (rawData ? { ...rawData, ...lead } : lead);
                        const sourceUrl = leadData.sourceUrl || leadData.link || leadData.url || '';
                        
                        return (
                          <LeadCard
                            key={id}
                            lead={lead}
                            isSelected={!isSignalsSearch && selectedLead === lead}
                            onSelect={() => {
                              // For signals, open article directly (feature parity with FeedHome). For others, select lead
                              if (isSignalsSearch) {
                                const articleUrl = leadData.sourceUrl || leadData.link || leadData.url || '';
                                if (articleUrl) {
                                  window.open(articleUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  setSignalPreview(lead);
                                }
                              } else {
                                setSelectedLead(lead);
                              }
                            }}
                            status={leadStatuses[id] || 'inbox'}
                            onStatusChange={(status) => updateLeadStatus(id, status)}
                            customFields={customFields}
                            isBulkMode={showBulkActions}
                            isBulkSelected={selectedLeads.includes(id)}
                            onBulkToggle={() => {
                              setSelectedLeads(prev => 
                                prev.includes(id) 
                                  ? prev.filter(x => x !== id)
                                  : [...prev, id]
                              );
                            }}
                            searchMode={feedMode}
                            onFindContacts={(company, contacts) => {
                              console.log('Found contacts at', company, contacts);
                            }}
                            onFindDecisionMaker={(company, dm) => {
                              console.log('Found decision maker at', company, dm);
                            }}
                            onRefineSearch={(context, data) => {
                              // Open refine panel with context from this result
                              setRefineQuery(context);
                              setShouldOpenRefinePanel(true);
                            }}
                            onFindSimilar={(filters, keyElements) => {
                              // Open refine slide-out with pre-filled filters
                              setRefineQuery(`${keyElements.jobTitle || ''} at ${keyElements.companyName || ''}`.trim() || 'Find similar');
                              setRefinePillFilters(filters);
                              setShouldOpenRefinePanel(true);
                            }}
                            cardIndex={index}
                          />
                        );
                      })}
                    </div>
                    {/* Load More Footer - handles both local UI pagination and server pagination */}
                    {(filteredLeads.length > loadedCount || leads.length < totalServerResults) && (
                      <LoadMoreFooter
                        currentCount={loadedCount}
                        totalAvailable={totalServerResults || filteredLeads.length}
                        onLoadMore={(increment) => {
                          const newLoadedCount = loadedCount + increment;
                          
                          // If we need more results than what's loaded from server, fetch more
                          if (newLoadedCount > leads.length && leads.length < totalServerResults) {
                            fetchMoreLeads();
                          }
                          
                          // Update local display count
                          setLoadedCount(Math.min(newLoadedCount, leads.length, totalServerResults || leads.length));
                          setHasMore(newLoadedCount < totalServerResults || newLoadedCount < filteredLeads.length);
                        }}
                        isLoading={isLoadingMore}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Lead Detail Panel - Full-screen drawer on mobile, sidebar on desktop */}
              {selectedLead && !isSignalsSearch && (
                <>
                  {/* Backdrop overlay for mobile drawer */}
                  {isMobile && (
                    <div 
                      className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
                      onClick={() => setSelectedLead(null)}
                      style={{ animation: 'eq-fade-in 200ms ease-out' }}
                    />
                  )}
                  {/* Drawer panel - full width on mobile for better UX */}
                  <div className={`${
                    isMobile 
                      ? 'fixed inset-x-0 bottom-0 top-14 z-50 shadow-2xl rounded-t-2xl' 
                      : 'w-[420px] max-w-[40%] flex-shrink-0 border-l border-black/5'
                  } bg-white overflow-hidden`}
                  style={isMobile ? { animation: 'eq-slide-up 300ms cubic-bezier(0.16, 1, 0.3, 1)' } : undefined}
                  >
                  <LeadDetailPanel 
                    lead={selectedLead} 
                    onClose={() => setSelectedLead(null)}
                    status={leadStatuses[selectedLead.id || selectedLead.personId || selectedLead.lead_id] || 'inbox'}
                    onStatusChange={(status) => updateLeadStatus(selectedLead.id || selectedLead.personId || selectedLead.lead_id, status)}
                    searchMode={feedMode}
                    isMobile={isMobile}
                  />
                </div>
                </>
              )}
            </>
          ) : (
            /* Kanban View */
            <KanbanView
              columns={kanbanColumns}
              statuses={LEAD_STATUSES}
              selectedLead={selectedLead}
              onSelectLead={setSelectedLead}
              onStatusChange={updateLeadStatus}
              isMobile={isMobile}
              mobileTab={mobileKanbanTab}
              onMobileTabChange={setMobileKanbanTab}
            />
          )}
        </div>
      </main>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          shareLink={shareLink}
          onClose={() => setShowShareModal(false)}
          onCopy={copyShareLink}
          listName={feed?.name}
          resultsCount={totalServerResults || leads.length}
        />
      )}

      {/* Signal Preview Modal - Quick preview before opening article */}
      {signalPreview && (
        <SignalPreviewModal
          signal={signalPreview}
          onClose={() => setSignalPreview(null)}
          onLike={() => {
            const id = signalPreview.id || signalPreview.personId || signalPreview.lead_id;
            updateLeadStatus(id, 'liked');
            setSignalPreview(null);
          }}
          onArchive={() => {
            const id = signalPreview.id || signalPreview.personId || signalPreview.lead_id;
            updateLeadStatus(id, 'archived');
            setSignalPreview(null);
          }}
          onSearch={(searchTerm) => {
            // Navigate to FeedHome with the search query
            setSignalPreview(null);
            navigate('/app', { state: { query: searchTerm } });
          }}
        />
      )}

      {/* Dashboard Modal */}
      <Dashboard
        isOpen={showDashboard}
        onClose={() => setShowDashboard(false)}
      />
      
      {/* BulkOutreachModal removed — outreach managed in Reach */}

      {/* Export Options Modal */}
      {showExportModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
          onClick={(e) => { if (e.target === e.currentTarget) setShowExportModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b border-black/5">
              <div>
                <h2 className="text-lg font-bold text-black">
                  Export {filteredLeads.length} Leads
                </h2>
                <p className="text-xs text-black/40 mt-0.5">17 standard fields always included</p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1.5 rounded-lg text-black/30 hover:text-black/60 hover:bg-black/5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <p className="text-xs font-medium text-black/50 uppercase tracking-wider">Include additional fields</p>
              {Object.entries(FIELD_GROUP_META).map(([key, meta]) => (
                <label
                  key={key}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    exportFieldGroups[key]
                      ? 'border-[var(--eq-purple)]/30 bg-[var(--eq-purple)]/[0.03]'
                      : 'border-black/5 hover:border-black/10'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={exportFieldGroups[key]}
                    onChange={(e) => setExportFieldGroups(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 rounded accent-[var(--eq-purple)]"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-black">{meta.label}</span>
                    <p className="text-xs text-black/40 mt-0.5">{meta.description}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex items-center justify-between p-5 border-t border-black/5 bg-black/[0.01]">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm text-black/50 hover:text-black/70 transition-colors"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => doExportWithGroups([])}
                  disabled={isExporting}
                  className="px-4 py-2 text-sm font-medium text-black/70 bg-black/5 hover:bg-black/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  Quick Export
                </button>
                <button
                  onClick={() => doExportWithGroups()}
                  disabled={isExporting}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[var(--eq-purple)] hover:bg-[var(--eq-purple)]/90 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
      </SplitPaneLayout>
    </SearchProvider>
  );
}

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

// Mobile Action Menu
function MobileActionMenu({ onExport, onShare, onDuplicate, onWatch, onRefresh, onDelete, onBulkToggle, isWatching, isRefreshing }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-slate-5 rounded-lg"
      >
        <MoreVertical className="w-5 h-5 text-slate-60" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-xl border border-slate-10 py-1 min-w-[180px]">
            <button onClick={() => { onBulkToggle(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              <Layers className="w-4 h-4" /> Bulk Actions
            </button>
            <button onClick={() => { onExport(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={() => { onShare(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={() => { onDuplicate(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              <Copy className="w-4 h-4" /> Duplicate
            </button>
            <button onClick={() => { onWatch(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              {isWatching ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              {isWatching ? 'Stop Watching' : 'Watch'}
            </button>
            <button onClick={() => { onRefresh(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-slate-70 hover:bg-slate-5 flex items-center gap-3">
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <hr className="my-1 border-slate-5" />
            <button onClick={() => { onDelete(); setIsOpen(false); }} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Empty State - Clean, shows status and provides actions
function EmptyState({ onRefresh, isRefreshing }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[var(--eq-purple)]/10 flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-[var(--eq-purple-text)]" />
      </div>
      <h3 className="text-lg font-semibold text-black mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
        No results yet
      </h3>
      <p className="text-sm text-black/50 mb-4">
        This agent was created but no results were saved. Try running the search again to populate results.
      </p>
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--eq-purple)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isRefreshing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running search...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              Run Search Again
            </>
          )}
        </button>
      )}
    </div>
  );
}

// LeadCard is now imported from ../shared/LeadCard

// LeadDetailPanel is now imported from ../shared/LeadDetailPanel

// Kanban View
function KanbanView({ columns, statuses, selectedLead, onSelectLead, onStatusChange, isMobile, mobileTab, onMobileTabChange }) {
  if (isMobile) {
    // Mobile: Tab-based navigation
    const currentStatus = statuses.find(s => s.id === mobileTab) || statuses[0];
    const currentLeads = columns[mobileTab] || [];
    
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tab Bar */}
        <div className="flex overflow-x-auto bg-white border-b border-slate-5 px-2 py-1 gap-1 flex-shrink-0">
          {statuses.map(status => (
            <button
              key={status.id}
              onClick={() => onMobileTabChange(status.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                mobileTab === status.id
                  ? `${status.bgLight} text-black`
                  : 'text-slate-50 hover:bg-slate-5'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${status.color}`} />
              {status.label}
              <span className="text-slate-40">({columns[status.id]?.length || 0})</span>
            </button>
          ))}
        </div>
        
        {/* Cards */}
        <div className="flex-1 overflow-y-auto p-3">
          {currentLeads.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-slate-40">No leads in {currentStatus.label}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {currentLeads.map((lead, i) => (
                <KanbanCard
                  key={lead.id || lead.personId || i}
                  lead={lead}
                  onSelect={() => onSelectLead(lead)}
                  onStatusChange={onStatusChange}
                  statuses={statuses}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Desktop: Horizontal columns
  return (
    <div className="flex-1 flex overflow-x-auto p-4 gap-4">
      {statuses.map(status => (
        <div
          key={status.id}
          className="w-72 flex-shrink-0 flex flex-col bg-slate-[0.02] rounded-xl"
        >
          {/* Column Header */}
          <div className="p-3 flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
            <span className="text-sm font-medium text-black">{status.label}</span>
            <span className="text-xs text-slate-40 ml-auto">
              {columns[status.id]?.length || 0}
            </span>
          </div>
          
          {/* Cards */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {(columns[status.id] || []).map((lead, i) => (
              <KanbanCard
                key={lead.id || lead.personId || i}
                lead={lead}
                onSelect={() => onSelectLead(lead)}
                onStatusChange={onStatusChange}
                statuses={statuses}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Kanban Card
function KanbanCard({ lead, onSelect, onStatusChange, statuses }) {
  const data = lead.data || lead.lead_data || lead;
  const id = lead.id || lead.personId || lead.lead_id;
  const name = data.name || data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown';
  const title = data.jobTitle || data.title || '';
  const company = data.companyName || data.company_name || '';
  const profilePic = data.profilePicture || data.profilePictureUrl || data.imageUrl;
  
  return (
    <div
      onClick={onSelect}
      className="bg-white rounded-lg border border-slate-5 p-3 cursor-pointer hover:shadow-md hover:border-[var(--eq-purple)]/30 transition-all"
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)]">
          {profilePic ? (
            <img src={profilePic} alt={name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-black truncate">{name}</p>
          <p className="text-xs text-slate-50 truncate">{title}</p>
          {company && <p className="text-xs text-slate-40 truncate">{company}</p>}
        </div>
      </div>
    </div>
  );
}

// Share Modal - Unified single-link design
// Recipients get preview → sign in → list saved to their account
function ShareModal({ shareLink, onClose, onCopy, listName, resultsCount }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header - Premium minimal */}
        <div className="bg-gradient-to-br from-[var(--eq-purple)] via-indigo-600 to-[#4338ca] p-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Send className="h-7 w-7" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-medium">Ready to share</p>
              <h3 className="text-xl font-semibold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {listName || "Your Research"}
              </h3>
            </div>
          </div>
        </div>
        
        {/* Content - Streamlined */}
        <div className="p-6 space-y-6">
          {/* Stats row */}
          {resultsCount > 0 && (
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-black/60">
                <Users className="h-4 w-4 text-[var(--eq-purple)]" />
                <span className="font-medium">{resultsCount} contacts</span>
              </div>
            </div>
          )}

          {/* Single share link */}
          <div>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 px-4 py-3.5 border border-black/10 rounded-xl text-sm bg-[#fafafa] text-black/80 font-mono truncate focus:outline-none focus:ring-2 focus:ring-[var(--eq-purple)]/20"
              />
              <button
                onClick={handleCopy}
                className={`px-5 py-3.5 rounded-xl font-semibold transition-all flex items-center gap-2 min-w-[100px] justify-center ${
                  copied
                    ? "bg-[#52dcb3] text-white"
                    : "bg-[var(--eq-purple)] text-white hover:shadow-lg hover:shadow-[#5a20ff]/25"
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          
          {/* How it works - Minimal */}
          <div className="bg-[#fafafa] rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wide">How it works</p>
            <div className="space-y-2.5">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[var(--eq-purple)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-[var(--eq-purple-text)]">1</span>
                </div>
                <p className="text-sm text-black/70">Recipients see a preview of your research</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[var(--eq-purple)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-[var(--eq-purple-text)]">2</span>
                </div>
                <p className="text-sm text-black/70">They sign in to unlock full contact details</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[var(--eq-teal)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="h-3 w-3 text-[var(--eq-teal)]" />
                </div>
                <p className="text-sm text-black/70">List is automatically saved to their account</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer - Clean CTA */}
        <div className="px-6 py-4 bg-[#fafafa] border-t border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] text-black/40">
            <div className="w-3 h-3 rounded bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)]" />
            <span>Powered by EQ.app</span>
          </div>
          <button
            onClick={() => {
              handleCopy();
              setTimeout(onClose, 600);
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white rounded-full font-semibold text-sm hover:shadow-lg hover:shadow-[#5a20ff]/25 transition-all hover:-translate-y-0.5"
          >
            Copy & Send
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Signal Preview Modal - Quick preview before opening article
// Company name and headline are clickable blue links to start new searches
// =============================================================================
function SignalPreviewModal({ signal, onClose, onLike, onArchive, onSearch }) {
  const data = signal.data || signal.lead_data || signal;
  
  const toText = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "object") {
      return value.name || value.title || value.label || value.source || fallback;
    }
    return fallback;
  };
  
  // Extract signal fields
  const headline = toText(data.headline || data.title || data.name);
  const snippet = toText(
    data.signalSnippet ||
      data.snippet ||
      data.body ||
      data.content ||
      data.highlight ||
      data.description ||
      data.summary
  );
  const signalType = toText(data.signalType || data.type, "News");
  const signalDate = toText(
    data.signalDate || data.date || data.publishedDate || data.published_at
  );
  const source = toText(data.signalSource || data.source || data.publisher);
  const sourceUrl =
    (typeof data.sourceUrl === "string" && data.sourceUrl) ||
    (typeof data.link === "string" && data.link) ||
    (typeof data.url === "string" && data.url) ||
    (typeof data.source?.link === "string" && data.source.link) ||
    (typeof data.source?.url === "string" && data.source.url) ||
    "";
  const sourceLogo =
    (typeof data.sourceLogo === "string" && data.sourceLogo) ||
    data.source?.icon ||
    data.source?.thumbnail ||
    data.thumbnail ||
    "";
  const companyName = toText(data.companyName || data.company || data.organization);
  const fundingAmount = toText(data.amount || data.fundingAmount || data.raised);
  
  // Check if new (within 24 hours)
  const isNew = React.useMemo(() => {
    if (!signalDate) return false;
    try {
      const articleDate = new Date(signalDate);
      const now = new Date();
      const hoursDiff = (now.getTime() - articleDate.getTime()) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    } catch {
      return false;
    }
  }, [signalDate]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isNew && (
              <span className="px-2 py-0.5 bg-red-500 text-white rounded text-xs font-bold">NEW</span>
            )}
            <span className="px-2 py-0.5 bg-[var(--eq-teal)]/20 text-[var(--eq-teal-text)] rounded text-xs font-semibold">{signalType}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-black/40" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Company & Funding - Company is clickable to search */}
          {(companyName || fundingAmount) && (
            <div className="flex items-center gap-2 mb-3">
              {companyName && (
                <button
                  onClick={() => {
                    if (onSearch) {
                      onSearch(`Company: ${companyName}`, { companyName });
                      onClose();
                    }
                  }}
                  className="text-sm font-semibold text-[var(--eq-purple-text)] hover:underline cursor-pointer"
                  title={`Search for "${companyName}"`}
                >
                  {companyName} →
                </button>
              )}
              {fundingAmount && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                  {fundingAmount}
                </span>
              )}
            </div>
          )}
          
          {/* Headline - Clickable to start new search */}
          <button
            onClick={() => {
              if (onSearch && headline) {
                onSearch(headline, { query: headline });
                onClose();
              }
            }}
            className="text-xl font-bold text-[var(--eq-purple-text)] mb-3 leading-tight text-left hover:underline cursor-pointer w-full"
            style={{ fontFamily: "'Playfair Display', serif" }}
            title="Click to search this topic"
          >
            {headline}
          </button>
          
          {/* Snippet */}
          {snippet && (
            <p className="text-sm text-black/70 leading-relaxed mb-4">
              {snippet}
            </p>
          )}
          
          {/* Source */}
          <div className="flex items-center gap-2 text-xs text-black/40 mt-4 pt-4 border-t border-black/5">
            {sourceLogo && (
              <img 
                src={sourceLogo} 
                alt={source}
                className="w-4 h-4 rounded object-contain"
                onError={(e) => { (e.target).style.display = 'none'; }}
              />
            )}
            <span>{source}</span>
            {signalDate && <span>· {signalDate}</span>}
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-5 py-4 border-t border-black/5 flex items-center gap-3">
          {/* Archive */}
          <button
            onClick={onArchive}
            className="flex items-center gap-2 px-4 py-2.5 bg-black/5 text-black/60 rounded-xl text-sm font-medium hover:bg-black/10 transition-colors"
          >
            <X className="w-4 h-4" />
            Skip
          </button>
          
          {/* Like */}
          <button
            onClick={onLike}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] rounded-xl text-sm font-medium hover:bg-[var(--eq-purple)]/20 transition-colors"
          >
            <Star className="w-4 h-4" />
            Save
          </button>
          
          {/* Open Article */}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Read Article
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
